import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { getJobProgress, getMetricsProgress } from "../../services/remote-api";
import { ModelViewer } from "../viewer/model-viewer";
import { ModelLoadError } from "../viewer/model-error";
import { PortraitLock } from "../portrait-lock";

// Presigned model URLs expire after ~10 minutes; refresh slightly earlier.
const MODEL_URL_TTL_MS = 9 * 60 * 1000;

// Review a saved scan from the dashboard in the model viewer.
//
// 'group' - the body group the scan belongs to.
// 'order' - contains the web order info, needed by the automatic measurement.
//
// If the saved presigned model URL has expired, a fresh one is fetched from the
// scan's job first (and handed back via onRefreshed for persisting).
//
// A scan that was already measured is reviewed as its measured mesh, with the
// saved sizes annotated: that URL is re-issued by the metrics job, and the
// measurement is not offered again. Measuring one here reports the result
// through onMeasured, to be saved with the scan.
export const ScanReview = ({
  scan,
  group,
  order,
  onClose,
  onDelete,
  onRefreshed,
  onMeasured,
}) => {
  // the measured mesh takes the place of the scanned model when there is one.
  const measurement = scan.measurement;
  const savedUrl = measurement ? measurement.modelUrl : scan.modelUrl;
  // when that URL was issued.
  const savedUrlAt = measurement ? measurement.modelUrlAt : scan.modelUrlAt;
  // the saved URL is only usable while it is fresh enough.
  const [url, setUrl] = useState(() =>
    Date.now() - (savedUrlAt || 0) < MODEL_URL_TTL_MS ? savedUrl : null,
  );
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0); // bumped to retry after an error

  // no usable URL — ask the job for a fresh presigned one.
  useEffect(() => {
    if (url) return undefined;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        // the measured mesh is the metrics job's first result file; the
        // scanned model is the vision job's.
        const status = measurement
          ? await getMetricsProgress(measurement.jobId)
          : await getJobProgress(scan.jobId);
        const urls = status.presignedUrls;
        const freshUrl = urls && urls.length ? urls[0] : null;
        if (!freshUrl) {
          throw new Error("The scan data is no longer available.");
        }
        if (cancelled) return;
        setUrl(freshUrl);
        onRefreshed(freshUrl);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, attempt, scan.jobId, measurement, onRefreshed]);

  if (url) {
    return (
      <>
        <PortraitLock />
        <ModelViewer
          url={url}
          title={scan.title}
          scanType={scan.scanType}
          useMetrics={group.useMetrics}
          measurement={measurement}
          metricsInput={{
            relatedLabId: order.labId,
            webOrderId: order.webOrderId,
            webOrderUserName: order.webOrderUserName,
            dataUrl: scan.dataUrl,
            dataFilename: scan.modelName,
          }}
          onMeasured={onMeasured}
          // a failed download is retried with a newly issued presigned URL.
          onReload={() => setUrl(null)}
          onDelete={onDelete}
          onSave={onClose}
          saveConfirm={false}
          // measuring replaces the model on screen, so closing the review
          // hands back a fresh snapshot for the dashboard grid.
          thumbnail
        />
      </>
    );
  }

  // the model URL could not be re-issued — the same failure UI the model
  // viewer might show.
  if (error) {
    return (
      <ModelLoadError
        fullScreen
        headline={`Failed to load the scan: ${error}`}
        detail="If retrying doesn't help, the scan may no longer be available 
          — delete it and scan again."
        onRetry={() => setAttempt((a) => a + 1)}
        onDelete={onDelete}
        onBack={onClose}
      />
    );
  }

  // refreshing the model URL
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        px: 4,
        textAlign: "center",
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">Loading the scan…</Typography>
    </Box>
  );
};

ScanReview.propTypes = {
  scan: PropTypes.object.isRequired,
  group: PropTypes.object.isRequired,
  order: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRefreshed: PropTypes.func.isRequired,
  onMeasured: PropTypes.func.isRequired,
};
